import { Component, OnInit, ViewChild, signal, input } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { dataStore, db } from 'src/app/app.component';
import { ucFirst } from 'src/app/globals';
import { Rate } from 'src/app/interfaces';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../../ride-preparation/dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { MatChip } from '@angular/material/chips';
import { NgStyle } from '@angular/common';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-rates',
  templateUrl: './rates.component.html',
  styleUrl: './rates.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatChip,
    NgStyle,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    MatProgressSpinner,
  ],
})
export class RatesComponent implements OnInit {
  parentData = input<{ processing: boolean }>(undefined);
  @ViewChild('table', { static: false }) table: MatTable<any>;
  rates = signal<Rate[]>([]);
  filteredRates = signal<Rate[]>([]);
  displayedColumns = ['name', 'credits', 'color', 'button'];
  totalRates = signal<number>(0);
  searchCtrl = new UntypedFormControl();
  organisationId = dataStore.selectedOrganisation.id;

  constructor(public dialog: MatDialog) {}

  ngOnInit(): void {
    onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/products`), orderBy('name')),
      (snapshot) => {
        const rates: Rate[] = [];
        this.totalRates.set(snapshot.size);
        snapshot.forEach((doc) => {
          const data = doc.data() as Rate;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          rates.push({ id, ...data });
        });
        this.rates.set(rates);
        this.filteredRates.set(this.filterrates(this.searchCtrl.value, rates));
        if (this.table) {
          this.table.renderRows();
        }
      },
    );
    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredRates.set(this.filterrates(value, this.rates()));
      if (this.table) {
        this.table.renderRows();
      }
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterrates(value: string, array: Rate[]): Rate[] {
    if (!value || value === '') {
      return array.sort(this.sortrates);
    }
    const filterValue = value.toLowerCase();
    const filteredArray = array.filter((rate) => rate.name.toLowerCase().includes(filterValue));
    return filteredArray.sort(this.sortrates);
  }

  sortrates(a: Rate, b: Rate) {
    return a.name.localeCompare(b.name, 'nl', {
      numeric: true,
      sensitivity: 'base',
    });
  }

  editItem(rate?: Rate) {
    const fields: Field[] = [
      {
        key: 'name',
        type: 'string',
        label: 'Tariefnaam',
        required: true,
      },
      {
        key: 'cost',
        type: 'numberInput',
        label: 'Credits kosten',
        required: true,
        min: 0,
        decimal: 2,
      },
      {
        key: 'color',
        type: 'color',
        label: 'Agendakleur',
        required: true,
      },
      {
        key: 'default',
        type: 'toggle',
        label: 'Standaard tarief',
        required: false,
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '400px',
      data: {
        item: rate,
        itemLabel: 'tarief',
        fields: fields,
        optionalData: {
          rates: this.rates(),
        },
      },
    });
  }

  getExportData() {
    const rates = this.rates().map((rate) => rate as Rate);
    const columns = [
      { name: 'Naam', itemProperty: 'name' },
      { name: 'Aantal Credits', itemProperty: 'cost' },
      { name: 'Kleur', itemProperty: 'color' },
      { name: 'Standaard tarief', itemProperty: 'default' },
    ];
    return { items: rates, columns: columns, options: { sortBy: 'Naam' } };
  }

  getImportConfig() {
    const columnSettings = [
      { columnName: 'naam', itemProperty: 'name', required: true },
      {
        columnName: 'aantal credits',
        itemProperty: 'cost',
        type: 'number',
        required: true,
        min: 0,
        decimal: 2,
      },
      { columnName: 'kleur', itemProperty: 'color', defaultValue: '#efefef' },
      {
        columnName: 'standaard tarief',
        itemProperty: 'default',
        defaultValue: false,
        type: 'boolean',
      },
    ];
    const existingData = this.rates().map((rate) => rate as Rate);

    return {
      tab: 'rates',
      uniqueKey: 'name',
      organisationId: this.organisationId,
      columnSettings: columnSettings,
      existingData: existingData,
    };
  }
}
